// AdminToolsPage.jsx

import React, { useState, useEffect } from 'react';
import API from '../services/api';

function AdminToolsPage() {
  // State to track which accordion section is expanded
  const [expandedTool, setExpandedTool] = useState(null);

  // ========== 1) USER MANAGEMENT ==========
  const [users, setUsers] = useState([]);
  const [newUserForm, setNewUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'THERAPIST',
    password: '',
  });

  // ========== 2) CLIENT MANAGEMENT ==========
  const [clients, setClients] = useState([]);
  const [newClientName, setNewClientName] = useState('');

  // ========== 3) DAILY LOGS ==========
  const [dailyLogs, setDailyLogs] = useState([]);
  const [logsStart, setLogsStart] = useState('');
  const [logsEnd, setLogsEnd] = useState('');

  // ========== 4) PAYROLL ==========
  const [payrolls, setPayrolls] = useState([]);

  // ========== 5) INVOICE ==========
  const [invoiceClientId, setInvoiceClientId] = useState('');
  const [invoiceStartDate, setInvoiceStartDate] = useState('');
  const [invoiceEndDate, setInvoiceEndDate] = useState('');
  const [invoiceData, setInvoiceData] = useState(null);

  // ========== 6) MESSAGES ==========
  const [messages, setMessages] = useState([]);
  const [newMessageForm, setNewMessageForm] = useState({
    title: '',
    content: '',
    imageUrl: '',
  });

  // ========== 7) EXCEL IMPORT/EXPORT ==========
  const [excelFile, setExcelFile] = useState(null);

  // ========== 8) THERAPIST GOALS MGMT ==========
  const [therapistList, setTherapistList] = useState([]);
  const [selectedTherapistId, setSelectedTherapistId] = useState('');
  const [goalsForm, setGoalsForm] = useState({
    weekly_session_goal: 0,
    monthly_session_goal: 0,
    weekly_revenue_goal: 0,
    monthly_revenue_goal: 0,
    weekly_session_bonus: 0,
    pay_split_percent: 50,
    color_code: '#FFFFFF', 
  });

  // ========== 9) THERAPIST ACTIVATION MGMT ==========
  const [allTherapists, setAllTherapists] = useState([]);
  const [activeTherapists, setActiveTherapists] = useState([]);
  const [inactiveTherapists, setInactiveTherapists] = useState([]);
  const [selectedActiveId, setSelectedActiveId] = useState(null);
  const [selectedInactiveId, setSelectedInactiveId] = useState(null);

  // ========== 10) CLIENT ACTIVATION MGMT ==========
  const [allClientsForActivation, setAllClientsForActivation] = useState([]);
  const [activeClientsForActivation, setActiveClientsForActivation] = useState([]);
  const [inactiveClientsForActivation, setInactiveClientsForActivation] = useState([]);
  const [selectedActiveClientId, setSelectedActiveClientId] = useState(null);
  const [selectedInactiveClientId, setSelectedInactiveClientId] = useState(null);

  // -----------------------------------------
  // Toggle which accordion section is open
  // -----------------------------------------
  const toggleTool = (toolName) => {
    setExpandedTool(expandedTool === toolName ? null : toolName);
  };

  // -----------------------------------------
  // 1) USER MANAGEMENT
  // -----------------------------------------
  const fetchUsers = async () => {
    try {
      const res = await API.get('/admin/users');
      setUsers(res.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      alert('Error fetching users');
    }
  };

  const handleCreateUser = async () => {
    try {
      await API.post('/admin/users', {
        role: newUserForm.role,
        firstName: newUserForm.first_name,
        lastName: newUserForm.last_name,
        email: newUserForm.email,
        password: newUserForm.password,
      });
      alert('User created successfully!');
      setNewUserForm({
        first_name: '',
        last_name: '',
        email: '',
        role: 'THERAPIST',
        password: '',
      });
      fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      alert('Error creating user');
    }
  };

  // -----------------------------------------
  // 2) CLIENT MANAGEMENT
  // -----------------------------------------
  const fetchClients = async () => {
    try {
      const res = await API.get('/admin/all-clients');
      setClients(res.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
      alert('Error fetching clients');
    }
  };

  const handleAddClient = async () => {
    if (!newClientName.trim()) {
      alert('Please enter a client name');
      return;
    }
    try {
      // There's no existing route in your code that says /admin/clients for creation,
      // so you might need to create that route server-side if you haven't.
      // Example:
      // POST /admin/clients { client_name }
      await API.post('/admin/clients', { client_name: newClientName.trim() });
      alert(`Created client: ${newClientName}`);
      setNewClientName('');
      fetchClients();
    } catch (err) {
      console.error('Error adding client:', err);
      alert('Error adding client');
    }
  };

  // -----------------------------------------
  // 3) DAILY LOGS
  // -----------------------------------------
  const fetchDailyLogs = async () => {
    if (!logsStart || !logsEnd) {
      alert('Pick a start and end date for logs');
      return;
    }
    try {
      const res = await API.get('/admin/daily-report/history', {
        params: { start: logsStart, end: logsEnd },
      });
      setDailyLogs(res.data);
    } catch (err) {
      console.error('Error fetching daily logs:', err);
      alert('Error fetching daily logs');
    }
  };

  // -----------------------------------------
  // 4) PAYROLL
  // -----------------------------------------
  const fetchPayrolls = async () => {
    try {
      const res = await API.get('/admin/payroll');
      setPayrolls(res.data);
    } catch (err) {
      console.error('Error fetching payroll:', err);
      alert('Error fetching payroll');
    }
  };

  // -----------------------------------------
  // 5) INVOICE
  // -----------------------------------------
  const fetchInvoice = async () => {
    if (!invoiceClientId || !invoiceStartDate || !invoiceEndDate) {
      alert('Please select client + date range');
      return;
    }
    try {
      const res = await API.get('/admin/client-invoice', {
        params: {
          clientId: invoiceClientId,
          startDate: invoiceStartDate,
          endDate: invoiceEndDate,
        },
      });
      setInvoiceData(res.data);
    } catch (err) {
      console.error('Error fetching invoice:', err);
      alert('Error fetching invoice');
    }
  };

  // -----------------------------------------
  // 6) MESSAGES
  // -----------------------------------------
  const fetchMessages = async () => {
    try {
      const res = await API.get('/admin/messages');
      setMessages(res.data);
    } catch (err) {
      console.error('Error fetching messages:', err);
      alert('Error fetching messages');
    }
  };

  const handleCreateMessage = async () => {
    try {
      await API.post('/admin/messages', newMessageForm);
      alert('Message created!');
      setNewMessageForm({ title: '', content: '', imageUrl: '' });
      fetchMessages();
    } catch (err) {
      console.error('Error creating message:', err);
      alert('Error creating message');
    }
  };

  // -----------------------------------------
  // 7) EXCEL IMPORT/EXPORT
  // -----------------------------------------
  const handleExportExcel = async () => {
    try {
      // Original route that exports partial data
      const res = await API.get('/admin/export-excel', {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', 'therapy_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error exporting Excel:', err);
      alert('Error exporting Excel file');
    }
  };

  // NEW: Export ALL
  const handleExportAllExcel = async () => {
    try {
      // New route that exports all tables
      const res = await API.get('/admin/export-excel-all', {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', 'full_backup.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error exporting ALL Excel:', err);
      alert('Error exporting ALL Excel file');
    }
  };

  const handleImportExcel = async () => {
    if (!excelFile) {
      alert('Please select an Excel file first.');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('excelFile', excelFile);

      // Original route that imports only the "Clients" sheet
      const res = await API.post('/admin/import-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(res.data.message);
      setExcelFile(null);
    } catch (err) {
      console.error('Error importing Excel:', err);
      alert('Error importing Excel file');
    }
  };

  // NEW: Import ALL
  const handleImportAllExcel = async () => {
    if (!excelFile) {
      alert('Please select an Excel file first.');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('excelFile', excelFile);

      // New route that imports all tables
      const res = await API.post('/admin/import-excel-all', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(res.data.message);
      setExcelFile(null);
    } catch (err) {
      console.error('Error importing ALL Excel:', err);
      alert('Error importing ALL Excel file');
    }
  };

  // -----------------------------------------
  // 8) THERAPIST GOALS MGMT
  // -----------------------------------------
  const fetchTherapistList = async () => {
    try {
      const res = await API.get('/admin/users'); // returns only ACTIVE users
      const allUsers = res.data || [];
      const therapists = allUsers.filter((u) => u.role === 'THERAPIST');
      setTherapistList(therapists);
    } catch (err) {
      console.error('Error fetching therapist list:', err);
      alert('Error fetching therapist list');
    }
  };

  const handleSelectTherapist = async (tid) => {
    setSelectedTherapistId(tid);
    if (!tid) return;

    try {
      const res = await API.get(`/admin/therapist-goals/${tid}`);
      const data = res.data;
      setGoalsForm({
        weekly_session_goal: data.weekly_session_goal || 0,
        monthly_session_goal: data.monthly_session_goal || 0,
        weekly_revenue_goal: data.weekly_revenue_goal || 0,
        monthly_revenue_goal: data.monthly_revenue_goal || 0,
        weekly_session_bonus: data.weekly_session_bonus || 0,
        pay_split_percent: data.pay_split_percent || 50,
        color_code: data.color_code || '#FFFFFF',
      });
    } catch (err) {
      console.error('Error fetching therapist goals:', err);
      alert('Error fetching therapist goals');
    }
  };

  const handleGoalsSubmit = async () => {
    if (!selectedTherapistId) {
      alert('Select a therapist first');
      return;
    }
    try {
      await API.post('/admin/therapist-goals', {
        therapistId: selectedTherapistId,
        weeklySessionGoal: goalsForm.weekly_session_goal,
        monthlySessionGoal: goalsForm.monthly_session_goal,
        weeklyRevenueGoal: goalsForm.weekly_revenue_goal,
        monthlyRevenueGoal: goalsForm.monthly_revenue_goal,
        weeklySessionBonus: goalsForm.weekly_session_bonus,
        paySplitPercent: goalsForm.pay_split_percent,
        colorCode: goalsForm.color_code,
      });
      alert('Goals updated successfully!');
    } catch (err) {
      console.error('Error saving therapist goals:', err);
      alert('Error saving therapist goals');
    }
  };

  // -----------------------------------------
  // 9) THERAPIST ACTIVATION MGMT
  // -----------------------------------------
  const fetchAllTherapists = async () => {
    try {
      const res = await API.get('/admin/all-therapists');
      const all = res.data;
      setAllTherapists(all);

      const active = all.filter((t) => t.is_active);
      const inactive = all.filter((t) => !t.is_active);
      setActiveTherapists(active);
      setInactiveTherapists(inactive);
    } catch (err) {
      console.error('Error fetching all therapists:', err);
      alert('Error fetching all therapists');
    }
  };

  const handleActivateTherapist = async (therapistId) => {
    try {
      await API.put(`/admin/users/${therapistId}/active`, { isActive: true });
      fetchAllTherapists();
    } catch (err) {
      console.error('Error activating therapist:', err);
      alert('Error activating therapist');
    }
  };

  const handleDeactivateTherapist = async (therapistId) => {
    try {
      await API.put(`/admin/users/${therapistId}/active`, { isActive: false });
      fetchAllTherapists();
    } catch (err) {
      console.error('Error deactivating therapist:', err);
      alert('Error deactivating therapist');
    }
  };

  // -----------------------------------------
  // 10) CLIENT ACTIVATION MGMT
  // -----------------------------------------
  const fetchAllClientsForActivation = async () => {
    try {
      const res = await API.get('/admin/all-clients-all');
      const all = res.data;
      setAllClientsForActivation(all);

      const active = all.filter((c) => c.is_active);
      const inactive = all.filter((c) => !c.is_active);
      setActiveClientsForActivation(active);
      setInactiveClientsForActivation(inactive);
    } catch (err) {
      console.error('Error fetching all clients (active/inactive):', err);
      alert('Error fetching all clients');
    }
  };

  const handleActivateClient = async (clientId) => {
    try {
      await API.put(`/admin/clients/${clientId}/active`, { isActive: true });
      fetchAllClientsForActivation();
    } catch (err) {
      console.error('Error activating client:', err);
      alert('Error activating client');
    }
  };

  const handleDeactivateClient = async (clientId) => {
    try {
      await API.put(`/admin/clients/${clientId}/active`, { isActive: false });
      fetchAllClientsForActivation();
    } catch (err) {
      console.error('Error deactivating client:', err);
      alert('Error deactivating client');
    }
  };

  // -----------------------------------------
  // useEffect watchers
  // -----------------------------------------
  useEffect(() => {
    if (expandedTool === 'users') {
      fetchUsers();
    }
    if (expandedTool === 'clients') {
      fetchClients();
    }
    if (expandedTool === 'logs') {
      // We only fetch logs after the user picks date range
    }
    if (expandedTool === 'payroll') {
      fetchPayrolls();
    }
    if (expandedTool === 'invoice') {
      fetchClients(); // to help pick a client if needed
    }
    if (expandedTool === 'messages') {
      fetchMessages();
    }
    if (expandedTool === 'excel') {
      // no immediate fetch needed
    }
    if (expandedTool === 'goals') {
      fetchTherapistList();
    }
    if (expandedTool === 'therapistActive') {
      fetchAllTherapists();
    }
    if (expandedTool === 'clientActive') {
      fetchAllClientsForActivation();
    }
    // eslint-disable-next-line
  }, [expandedTool]);

  return (
    <div style={{ margin: '2rem' }}>
      <h2>Admin Tools</h2>
      <p>Select a tool below to expand and perform operations.</p>

      {/* ===== USER MANAGEMENT ===== */}
      <div style={styles.section}>
        <div onClick={() => toggleTool('users')} style={styles.sectionHeader}>
          <h3>User Management</h3>
        </div>
        {expandedTool === 'users' && (
          <div style={styles.sectionBody}>
            <h4>Existing Users:</h4>
            <ul>
              {users.map((u) => (
                <li key={u.id}>
                  {u.first_name} {u.last_name} ({u.role}) - {u.email}
                </li>
              ))}
            </ul>

            <h4>Create New User</h4>
            <div>
              <label>First Name:</label>
              <input
                type="text"
                value={newUserForm.first_name}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, first_name: e.target.value })
                }
              />

              <label>Last Name:</label>
              <input
                type="text"
                value={newUserForm.last_name}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, last_name: e.target.value })
                }
              />

              <label>Email:</label>
              <input
                type="text"
                value={newUserForm.email}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, email: e.target.value })
                }
              />

              <label>Role:</label>
              <select
                value={newUserForm.role}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, role: e.target.value })
                }
              >
                <option value="THERAPIST">THERAPIST</option>
                <option value="OFFICE_ADMIN">OFFICE_ADMIN</option>
                <option value="PORTAL_ADMIN">PORTAL_ADMIN</option>
              </select>

              <label>Password:</label>
              <input
                type="password"
                value={newUserForm.password}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, password: e.target.value })
                }
              />

              <button onClick={handleCreateUser}>Create User</button>
            </div>
          </div>
        )}
      </div>

      {/* ===== CLIENT MANAGEMENT ===== */}
      <div style={styles.section}>
        <div onClick={() => toggleTool('clients')} style={styles.sectionHeader}>
          <h3>Client Management</h3>
        </div>
        {expandedTool === 'clients' && (
          <div style={styles.sectionBody}>
            <h4>Existing Clients</h4>
            <ul>
              {clients.map((c) => (
                <li key={c.id}>{c.client_name}</li>
              ))}
            </ul>

            <h4>Add New Client</h4>
            <div>
              <input
                type="text"
                placeholder="Client Name"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
              />
              <button onClick={handleAddClient}>Add Client</button>
            </div>
          </div>
        )}
      </div>

      {/* ===== DAILY LOGS ===== */}
      <div style={styles.section}>
        <div onClick={() => toggleTool('logs')} style={styles.sectionHeader}>
          <h3>Daily Logs / Service Entries</h3>
        </div>
        {expandedTool === 'logs' && (
          <div style={styles.sectionBody}>
            <div>
              <label>Start Date:</label>
              <input
                type="date"
                value={logsStart}
                onChange={(e) => setLogsStart(e.target.value)}
              />
              <label>End Date:</label>
              <input
                type="date"
                value={logsEnd}
                onChange={(e) => setLogsEnd(e.target.value)}
              />
              <button onClick={fetchDailyLogs}>Fetch Logs</button>
            </div>
            <div className="table-scroll">
            <table border="1" cellPadding="4" style={{ marginTop: '1rem', minWidth: '560px' }}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Session Date</th>
                  <th>Client</th>
                  <th>Therapist</th>
                  <th>Billed</th>
                  <th>Collected</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dailyLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.id}</td>
                    <td>{log.session_date}</td>
                    <td>{log.client_name}</td>
                    <td>
                      {log.therapist_first_name} {log.therapist_last_name}
                    </td>
                    <td>{log.billed_amount}</td>
                    <td>{log.collected_amount}</td>
                    <td>{log.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* ===== PAYROLL ===== */}
      <div style={styles.section}>
        <div onClick={() => toggleTool('payroll')} style={styles.sectionHeader}>
          <h3>Payroll Management</h3>
        </div>
        {expandedTool === 'payroll' && (
          <div style={styles.sectionBody}>
            <button onClick={fetchPayrolls}>Fetch Payroll Summaries</button>
            <table border="1" cellPadding="4" style={{ marginTop: '1rem' }}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Therapist</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Total Collected</th>
                  <th>Approved?</th>
                </tr>
              </thead>
              <tbody>
                {payrolls.map((p) => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>
                      {p.first_name} {p.last_name}
                    </td>
                    <td>{p.start_date}</td>
                    <td>{p.end_date}</td>
                    <td>{p.total_collected}</td>
                    <td>{p.approved_at ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== INVOICE ===== */}
      <div style={styles.section}>
        <div onClick={() => toggleTool('invoice')} style={styles.sectionHeader}>
          <h3>Invoice Generation</h3>
        </div>
        {expandedTool === 'invoice' && (
          <div style={styles.sectionBody}>
            <p>Select client + date range to generate invoice.</p>
            <div>
              <label>Client ID:</label>
              <input
                type="text"
                value={invoiceClientId}
                onChange={(e) => setInvoiceClientId(e.target.value)}
              />
            </div>
            <div>
              <label>Start Date:</label>
              <input
                type="date"
                value={invoiceStartDate}
                onChange={(e) => setInvoiceStartDate(e.target.value)}
              />
              <label>End Date:</label>
              <input
                type="date"
                value={invoiceEndDate}
                onChange={(e) => setInvoiceEndDate(e.target.value)}
              />
            </div>
            <button onClick={fetchInvoice}>Generate Invoice</button>

            {invoiceData && (
              <div style={{ marginTop: '1rem' }}>
                <h4>Invoice for {invoiceData.clientName}</h4>
                <p>
                  {invoiceData.startDate} to {invoiceData.endDate}
                </p>
                <table border="1" cellPadding="4">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Service</th>
                      <th>Therapist</th>
                      <th>Billed</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.sessions.map((s, idx) => (
                      <tr key={idx}>
                        <td>{s.session_date}</td>
                        <td>{s.service_type}</td>
                        <td>{s.therapist_name}</td>
                        <td>{s.billed_amount}</td>
                        <td>{s.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== MESSAGES ===== */}
      <div style={styles.section}>
        <div onClick={() => toggleTool('messages')} style={styles.sectionHeader}>
          <h3>Messages / Announcements</h3>
        </div>
        {expandedTool === 'messages' && (
          <div style={styles.sectionBody}>
            <button onClick={fetchMessages}>Reload Messages</button>
            <ul>
              {messages.map((m) => (
                <li key={m.id}>
                  <strong>{m.title}</strong> - {m.content}
                </li>
              ))}
            </ul>

            <h4>Create New Message</h4>
            <div>
              <label>Title:</label>
              <input
                type="text"
                value={newMessageForm.title}
                onChange={(e) =>
                  setNewMessageForm({ ...newMessageForm, title: e.target.value })
                }
              />
              <label>Content:</label>
              <input
                type="text"
                value={newMessageForm.content}
                onChange={(e) =>
                  setNewMessageForm({ ...newMessageForm, content: e.target.value })
                }
              />
              <label>Image URL:</label>
              <input
                type="text"
                value={newMessageForm.imageUrl}
                onChange={(e) =>
                  setNewMessageForm({
                    ...newMessageForm,
                    imageUrl: e.target.value,
                  })
                }
              />
              <button onClick={handleCreateMessage}>Post Message</button>
            </div>
          </div>
        )}
      </div>

      {/* ===== DATA IMPORT/EXPORT (EXCEL) ===== */}
      <div style={styles.section}>
        <div onClick={() => toggleTool('excel')} style={styles.sectionHeader}>
          <h3>Data Import / Export (Excel)</h3>
        </div>
        {expandedTool === 'excel' && (
          <div style={styles.sectionBody}>
            <h4>Export Data to Excel</h4>
            <p>This exports **Clients, Users, and Daily Logs** into one workbook.</p>
            <button onClick={handleExportExcel}>Export Excel</button>

            <h4 style={{ marginTop: '1rem' }}>Export ALL DB Tables</h4>
            <p>Calls the new /admin/export-excel-all route for a full backup.</p>
            <button onClick={handleExportAllExcel}>Export ALL Excel</button>

            <hr style={{ margin: '1rem 0' }} />

            <h4>Import Clients from Excel (Original)</h4>
            <p>
              The uploaded .xlsx must have a sheet named <strong>"Clients"</strong> with
              columns <em>ID</em> | <em>Client Name</em>. Rows will be inserted
              into the <strong>clients</strong> table.
            </p>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  setExcelFile(e.target.files[0]);
                }
              }}
            />
            {excelFile && <div>Selected File: {excelFile.name}</div>}

            <button onClick={handleImportExcel} style={{ marginTop: '0.5rem' }}>
              Import Excel
            </button>

            <h4 style={{ marginTop: '1rem' }}>Import ALL Tables from Excel</h4>
            <p>Calls the new /admin/import-excel-all route to insert data into every table.</p>
            <button onClick={handleImportAllExcel} style={{ marginTop: '0.5rem' }}>
              Import ALL Excel
            </button>
          </div>
        )}
      </div>

      {/* ===== THERAPIST GOALS ===== */}
      <div style={styles.section}>
        <div onClick={() => toggleTool('goals')} style={styles.sectionHeader}>
          <h3>Therapist Goals Management</h3>
        </div>
        {expandedTool === 'goals' && (
          <div style={styles.sectionBody}>
            <p>Pick a therapist, load existing goals, and update them.</p>
            <div>
              <label>Therapist:</label>
              <select
                value={selectedTherapistId}
                onChange={(e) => handleSelectTherapist(e.target.value)}
              >
                <option value="">--Select--</option>
                {therapistList.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.first_name} {t.last_name} (ID {t.id})
                  </option>
                ))}
              </select>
            </div>

            {selectedTherapistId && (
              <div style={{ marginTop: '1rem' }}>
                <div>
                  <label>Weekly Session Goal:</label>
                  <input
                    type="number"
                    value={goalsForm.weekly_session_goal}
                    onChange={(e) =>
                      setGoalsForm({
                        ...goalsForm,
                        weekly_session_goal: +e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label>Monthly Session Goal:</label>
                  <input
                    type="number"
                    value={goalsForm.monthly_session_goal}
                    onChange={(e) =>
                      setGoalsForm({
                        ...goalsForm,
                        monthly_session_goal: +e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label>Weekly Revenue Goal:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={goalsForm.weekly_revenue_goal}
                    onChange={(e) =>
                      setGoalsForm({
                        ...goalsForm,
                        weekly_revenue_goal: +e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label>Monthly Revenue Goal:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={goalsForm.monthly_revenue_goal}
                    onChange={(e) =>
                      setGoalsForm({
                        ...goalsForm,
                        monthly_revenue_goal: +e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label>Weekly Session Bonus:</label>
                  <input
                    type="number"
                    value={goalsForm.weekly_session_bonus}
                    onChange={(e) =>
                      setGoalsForm({
                        ...goalsForm,
                        weekly_session_bonus: +e.target.value,
                      })
                    }
                  />
                </div>

                {/* Pay Split % */}
                <div>
                  <label>Pay Split %:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={goalsForm.pay_split_percent}
                    onChange={(e) =>
                      setGoalsForm({
                        ...goalsForm,
                        pay_split_percent: +e.target.value,
                      })
                    }
                  />
                </div>

                {/* Color Code */}
                <div>
                  <label>Therapist Color:</label>
                  <input
                    type="color"
                    value={goalsForm.color_code}
                    onChange={(e) =>
                      setGoalsForm({ ...goalsForm, color_code: e.target.value })
                    }
                    style={{ width: '50px', height: '30px' }}
                  />
                </div>

                <button onClick={handleGoalsSubmit} style={{ marginTop: '0.75rem' }}>
                  Save Goals
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== THERAPIST ACTIVATION ===== */}
      <div style={styles.section}>
        <div onClick={() => toggleTool('therapistActive')} style={styles.sectionHeader}>
          <h3>Therapist Activation Management</h3>
        </div>
        {expandedTool === 'therapistActive' && (
          <div style={styles.sectionBody}>
            <p>Move therapists between Active and Inactive status.</p>
            <button onClick={fetchAllTherapists} style={{ marginBottom: '1rem' }}>
              Reload Therapists
            </button>

            <div style={{ display: 'flex', gap: '2rem' }}>
              {/* ACTIVE THERAPISTS BOX */}
              <div>
                <h4>Active Therapists</h4>
                <select
                  size={10}
                  style={{ width: '200px' }}
                  onChange={(e) => setSelectedActiveId(e.target.value)}
                >
                  {activeTherapists.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.first_name} {t.last_name} (ID {t.id})
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: '0.5rem' }}>
                  <button
                    onClick={() => {
                      if (!selectedActiveId) {
                        alert('Select an active therapist first');
                        return;
                      }
                      handleDeactivateTherapist(selectedActiveId);
                    }}
                  >
                    Deactivate &gt;&gt;
                  </button>
                </div>
              </div>

              {/* INACTIVE THERAPISTS BOX */}
              <div>
                <h4>Inactive Therapists</h4>
                <select
                  size={10}
                  style={{ width: '200px' }}
                  onChange={(e) => setSelectedInactiveId(e.target.value)}
                >
                  {inactiveTherapists.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.first_name} {t.last_name} (ID {t.id})
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: '0.5rem' }}>
                  <button
                    onClick={() => {
                      if (!selectedInactiveId) {
                        alert('Select an inactive therapist first');
                        return;
                      }
                      handleActivateTherapist(selectedInactiveId);
                    }}
                  >
                    &lt;&lt; Activate
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== CLIENT ACTIVATION ===== */}
      <div style={styles.section}>
        <div onClick={() => toggleTool('clientActive')} style={styles.sectionHeader}>
          <h3>Client Activation Management</h3>
        </div>
        {expandedTool === 'clientActive' && (
          <div style={styles.sectionBody}>
            <p>Move clients between Active and Inactive status.</p>
            <button onClick={fetchAllClientsForActivation} style={{ marginBottom: '1rem' }}>
              Reload Clients
            </button>

            <div style={{ display: 'flex', gap: '2rem' }}>
              {/* ACTIVE CLIENTS BOX */}
              <div>
                <h4>Active Clients</h4>
                <select
                  size={10}
                  style={{ width: '200px' }}
                  onChange={(e) => setSelectedActiveClientId(e.target.value)}
                >
                  {activeClientsForActivation.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.client_name} (ID {c.id})
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: '0.5rem' }}>
                  <button
                    onClick={() => {
                      if (!selectedActiveClientId) {
                        alert('Select an active client first');
                        return;
                      }
                      handleDeactivateClient(selectedActiveClientId);
                    }}
                  >
                    Deactivate &gt;&gt;
                  </button>
                </div>
              </div>

              {/* INACTIVE CLIENTS BOX */}
              <div>
                <h4>Inactive Clients</h4>
                <select
                  size={10}
                  style={{ width: '200px' }}
                  onChange={(e) => setSelectedInactiveClientId(e.target.value)}
                >
                  {inactiveClientsForActivation.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.client_name} (ID {c.id})
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: '0.5rem' }}>
                  <button
                    onClick={() => {
                      if (!selectedInactiveClientId) {
                        alert('Select an inactive client first');
                        return;
                      }
                      handleActivateClient(selectedInactiveClientId);
                    }}
                  >
                    &lt;&lt; Activate
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Basic styling for each accordion section
const styles = {
  section: {
    border: '1px solid var(--border-color)',
    marginBottom: '1rem',
    borderRadius: '4px',
  },
  sectionHeader: {
    background: 'var(--border-color)',
    padding: '0.5rem',
    cursor: 'pointer',
  },
  sectionBody: {
    padding: '1rem',
  },
};

export default AdminToolsPage;
